/**
 * Finds the nearest DOM element with a specified tag name starting from the given element.
 *
 * @param {HTMLElement} el - The starting element.
 * @param {string} tagName - The tag name to search for.
 * @returns {HTMLElement} - The nearest DOM element with the specified tag name.
 */
export const findNearestDOM = (el, tagName) => {
  let node = el;
  while (node.tagName !== tagName && node.tagName !== "BODY") {
    node = node.parentNode;
  }
  return node;
};

/**
 * Recursively finds the index path of a DOM element in a nested data structure.
 *
 * @param {HTMLElement} el - The DOM element to search for.
 * @param {Array} data - The nested data structure.
 * @param {Array<number>} [path=[]] - The current path (used for recursion).
 * @returns {Array<number>|null} - The index path of the element, or null if not found.
 */
export const findIndexRecursive = (el, data, path = []) => {
  for (let i = 0; i < data.length; i++) {
    const currentItem = data[i];
    const row = document.querySelector(`.data-id-${currentItem.id}`);
    if (row === el) return [...path, i];

    if (currentItem.childitems?.length) {
      const childPath = findIndexRecursive(el, currentItem.childitems, [
        ...path,
        i,
      ]);
      if (childPath) return childPath;
    }
  }
  return null;
};

/**
 * Retrieves an item from a nested data structure using its index path.
 *
 * @param {Array} data - The nested data structure.
 * @param {Array<number>} path - The index path of the item.
 * @returns {Object} - The retrieved item.
 */
export const getItemFromPath = (data, path) => {
  let current = data;
  for (const index of path.slice(0, -1)) {
    current = current[index].childitems;
  }
  return current[path[path.length - 1]];
};

/**
 * Removes an item from a nested data structure using its index path.
 *
 * @param {Array} data - The nested data structure.
 * @param {Array<number>} path - The index path of the item to remove.
 * @returns {Array} - The updated data structure.
 */
export const removeItemFromPath = (data, path) => {
  let current = data;
  for (const index of path.slice(0, -1)) {
    current = current[index].childitems;
  }
  current.splice(path[path.length - 1], 1);
  return data;
};

/**
 * Inserts an item into a nested data structure at the specified index path.
 *
 * @param {Array} data - The nested data structure.
 * @param {Array<number>} path - The index path where the item should be inserted.
 * @param {Object} item - The item to insert.
 * @returns {Array} - The updated data structure.
 */
export const insertItemAtPath = (data, path, item) => {
  let current = data;
  for (const index of path.slice(0, -1)) {
    current = current[index].childitems;
  }
  current.splice(path[path.length - 1], 0, item);
  return data;
};
